import {
  AGGREGATE_COUNCIL_SLUG,
  getCouncilConfig,
  isAggregateCouncilScope,
} from '../councils';
import {
  PLANNING_AREAS,
  buildSelectablePlanningAreaPath,
  getPlanningAreaById,
  searchPlanningAreas,
  type PlanningArea,
  type PlanningAreaRouteSurface,
} from '../planningAreas';
import { isShortLetsSearchQuery } from './shortLetsSearch';

export const PLANNING_SEARCH_REGION_SUGGESTION_PREFIX = 'planning-region:';

const SHORT_LETS_NORMALIZED_PHRASES = [
  'short term lets',
  'short term let',
  'short lets',
  'short let',
  'holiday lets',
  'holiday let',
  'airbnb',
  'stl',
];

const INTENT_TYPO_CORRECTIONS: Record<string, string> = {
  edinbugrh: 'edinburgh',
};

const normalizeIntentText = (value: string) => {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized
    .split(' ')
    .map((token) => INTENT_TYPO_CORRECTIONS[token] || token)
    .join(' ');
};

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const phrasePattern = (phrase: string) =>
  new RegExp(`(^|\\s)${escapeRegex(phrase)}(?=\\s|$)`, 'g');

const removePhrase = (value: string, phrase: string) =>
  value.replace(phrasePattern(phrase), ' ').replace(/\s+/g, ' ').trim();

const isRegionSearchArea = (area: PlanningArea | null | undefined) =>
  Boolean(area && area.active && area.selectable && (area.kind === 'country' || area.kind === 'council'));

const getRegionAliasPhrases = (area: PlanningArea) => {
  const council = area.councilSlug ? getCouncilConfig(area.councilSlug) : null;
  const phrases = [
    area.displayName,
    area.name,
    area.slug,
    ...(area.aliases || []),
    council?.shortCode,
    council?.displayName,
    council?.officialName,
  ];

  if (area.id === AGGREGATE_COUNCIL_SLUG || isAggregateCouncilScope(area.id)) {
    phrases.push('all', 'all regions', 'all areas', 'scotland');
  }

  return Array.from(new Set(
    phrases
      .map((phrase) => normalizeIntentText(String(phrase || '')))
      .filter(Boolean)
  )).sort((left, right) => right.length - left.length);
};

export type PlanningSearchIntent = {
  originalQuery: string;
  residualQuery: string;
  residualTokens: string[];
  region: PlanningArea | null;
  matchedRegionPhrase: string | null;
  includesShortLets: boolean;
  recognized: boolean;
};

export const shouldApplyPlanningSearchRegionDirectly = (
  intent: Pick<PlanningSearchIntent, 'region' | 'residualQuery'>,
) => Boolean(intent.region);

export const getPlanningSearchRegionSuggestionId = (area: PlanningArea) =>
  `${PLANNING_SEARCH_REGION_SUGGESTION_PREFIX}${area.id}`;

export const getPlanningSearchRegionFromSuggestionId = (id?: string | null) => {
  const raw = String(id || '');
  if (!raw.startsWith(PLANNING_SEARCH_REGION_SUGGESTION_PREFIX)) return null;
  const areaId = raw.slice(PLANNING_SEARCH_REGION_SUGGESTION_PREFIX.length);
  const area = getPlanningAreaById(areaId);
  return isRegionSearchArea(area) ? area : null;
};

export const buildPlanningSearchAreaPath = (
  surface: PlanningAreaRouteSurface,
  area: PlanningArea | null,
) => buildSelectablePlanningAreaPath(surface, area);

export const searchPlanningSearchRegions = (query: string, limit = 4) =>
  searchPlanningAreas(query, limit)
    .filter(isRegionSearchArea)
    .slice(0, limit);

export const resolvePlanningSearchRegionIntent = (value: string) => {
  const normalizedQuery = normalizeIntentText(value);
  if (!normalizedQuery) return null;

  const candidates = PLANNING_AREAS
    .filter(isRegionSearchArea)
    .flatMap((area) => getRegionAliasPhrases(area).map((phrase) => {
      const exact = normalizedQuery === phrase;
      const matches = exact || phrasePattern(phrase).test(normalizedQuery);
      if (!matches) return null;
      return {
        area,
        phrase,
        exact,
        wordCount: phrase.split(/\s+/).filter(Boolean).length,
        length: phrase.length,
      };
    }))
    .filter((entry): entry is {
      area: PlanningArea;
      phrase: string;
      exact: boolean;
      wordCount: number;
      length: number;
    } => Boolean(entry));

  if (candidates.length === 0) return null;

  candidates.sort((left, right) => (
    Number(right.exact) - Number(left.exact) ||
    right.wordCount - left.wordCount ||
    right.length - left.length ||
    left.area.displayName.localeCompare(right.area.displayName)
  ));

  return candidates[0];
};

export const parsePlanningSearchIntent = (value: string | string[]): PlanningSearchIntent => {
  const originalTokens = (Array.isArray(value) ? value : [value])
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  const originalQuery = originalTokens.join(' ').trim();
  const regionMatch = resolvePlanningSearchRegionIntent(originalQuery);
  const includesShortLets = isShortLetsSearchQuery(originalQuery);

  let residual = normalizeIntentText(originalQuery);
  if (regionMatch?.phrase) {
    residual = removePhrase(residual, regionMatch.phrase);
  }
  if (includesShortLets) {
    SHORT_LETS_NORMALIZED_PHRASES.forEach((phrase) => {
      residual = removePhrase(residual, phrase);
    });
  }

  const recognized = Boolean(regionMatch || includesShortLets);
  return {
    originalQuery,
    residualQuery: recognized ? residual : originalQuery,
    residualTokens: recognized ? (residual ? [residual] : []) : originalTokens,
    region: regionMatch?.area || null,
    matchedRegionPhrase: regionMatch?.phrase || null,
    includesShortLets,
    recognized,
  };
};
